import React, { useState, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';

import { Grid, Divider, TextField, Button, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import AuthCard from 'components/Cards/AuthCard';

import _ from 'lodash';
import { useUsers } from 'hooks';
import queryString from 'query-string';
import jwt_decode from 'jwt-decode';

import { useLocation } from 'react-router-dom';
import { RegEx } from 'utils';
import RegisterError from './RegisterError';
import RegistrationSuccess from './RegistrationSuccess';
import { AuthContext } from 'context/AuthContext';

import { CircularProgress } from '@mui/material';

const useStyles = makeStyles((theme) => ({
  footer: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(2),
  },
  registerCard: {
    width: 650,
    marginTop: 0,
  },
  formGroupGrid: {
    width: '100%',
    flexGrow: 1,
  },
}));

const RegisterForm = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const location = useLocation();
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const { userCompleteRegistration, getError, isFetchingUsers, clearErrors } = useUsers();
  const { logout } = useContext(AuthContext);

  const parsed = queryString.parse(location.search);
  let decoded = {};
  if (parsed?.ac) decoded = jwt_decode(parsed.ac);
  const [newUser, setNewUser] = useState({
    user_id: decoded?.sub,
    truth_email: decoded?.email,
    email: '',
    givenName: '',
    familyName: '',
    password: '',
    confirmPassword: '',
  });

  const handleRegister = (token, errors) => {
    if (!_.some(errors)) {
      clearErrors();
      userCompleteRegistration(
        {
          user_id: newUser.user_id,
          first_name: newUser.givenName,
          last_name: newUser.familyName,
          email: newUser.email,
          password: newUser.password,
        },
        token
      ).then((response) => {
        if (response && !response.error) {
          setSuccess(true);
        }
      });
    }
  };

  const handleFieldChange = (event, field, value) => {
    const newValues = {
      [field]: value,
    };
    if (event.persist) {
      setNewUser(Object.assign({}, newUser, newValues));
    }
    verifyFormData(field, value);
  };

  const verifyFormHasValues = () => {
    const e = _.every(newUser, (value) => {
      return value !== undefined && value !== '';
    });

    return e;
  };
  const verifyFormData = (field, value) => {
    const newErrors = {};

    if (field === 'givenName') {
      if (!value) {
        newErrors.givenName = t('required');
      } else if (errors.givenName) {
        newErrors.givenName = undefined;
      }
    }

    if (field === 'familyName') {
      if (!value) {
        newErrors.familyName = t('required');
      } else if (errors.familyName) {
        newErrors.familyName = undefined;
      }
    }

    if (field === 'email') {
      if (!value) {
        newErrors.email = t('required');
      } else if (!RegEx.isEmail(value)) {
        newErrors.email = t('invalid_email');
      } else if (value.toLowerCase() !== newUser.truth_email.toLowerCase()) {
        newErrors.email = t('email_mismatch');
      } else if (errors.email) {
        newErrors.email = undefined;
      }
    }

    if (field === 'password') {
      if (!value) {
        newErrors.password = t('required');
      } else if (errors.password) {
        newErrors.password = undefined;
      }
    }

    if (field === 'confirmPassword') {
      if (!value) {
        newErrors.confirmPassword = t('required');
      } else if (value !== newUser.password) {
        newErrors.confirmPassword = t('password_mismatch');
      } else if (errors.confirmPassword) {
        newErrors.confirmPassword = undefined;
      }
    }

    setErrors(newErrors);
    let valid = true;

    _.forEach(newErrors, (value) => {
      if (value !== undefined) {
        valid = false;
      }
    });

    return valid;
  };

  const hasValues = verifyFormHasValues();
  const footer = !isFetchingUsers ? (
    !success ? (
      <div className={classes.footer}>
        <Button
          color="primary"
          disabled={_.some(errors) || !hasValues}
          onClick={() => {
            handleRegister(parsed, errors);
          }}
        >
          {t('submit')}
        </Button>
      </div>
    ) : (
      <div className={classes.footer}>
        <Button variant="contained" size="large" color="primary" disableElevation onClick={logout}>
          {t('to_login')}
        </Button>
      </div>
    )
  ) : (
    <div className={classes.footer}>
      <CircularProgress />
    </div>
  );

  return (
    <form>
      <AuthCard errorMessage={errors && t(_.snakeCase(errors.errorMessage))} footer={footer}>
        {!success ? (
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom>
                {t('register')}
              </Typography>
              <Typography variant="body2" gutterBottom>
                {t('register_instructions')}
              </Typography>
              {getError && <RegisterError error={getError} />}
            </Grid>
            <Grid item xs={6}>
              <div className={classes.formGroupGrid}>
                <TextField
                  fullWidth
                  label={t('given_name')}
                  name="givenName"
                  autoComplete="given-name"
                  onChange={(event) => handleFieldChange(event, 'givenName', event.target.value)}
                  value={newUser?.givenName}
                  variant="outlined"
                  placeholder={t('given_name')}
                  helperText={errors && errors.givenName}
                  error={!!(errors && errors.givenName)}
                  inputProps={{
                    maxLength: 144,
                  }}
                  required
                />
              </div>
            </Grid>
            <Grid item xs={6}>
              <div className={classes.formGroupGrid}>
                <TextField
                  fullWidth
                  label={t('family_name')}
                  name="familyName"
                  autoComplete="family-name"
                  onChange={(event) => handleFieldChange(event, 'familyName', event.target.value)}
                  value={newUser?.familyName}
                  variant="outlined"
                  placeholder={t('family_name')}
                  helperText={errors && errors.familyName}
                  error={!!(errors && errors.familyName)}
                  inputProps={{
                    maxLength: 144,
                  }}
                  required
                />
              </div>
            </Grid>
            <Grid item xs={12}>
              <div className={classes.formGroupGrid}>
                <TextField
                  fullWidth
                  label={t('email')}
                  name="email"
                  autoComplete="email"
                  onChange={(event) => handleFieldChange(event, 'email', event.target.value)}
                  value={newUser?.email}
                  variant="outlined"
                  placeholder={t('email')}
                  helperText={errors && errors.email}
                  error={!!(errors && errors.email)}
                  inputProps={{
                    maxLength: 255,
                  }}
                  required
                />
              </div>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={6}>
              <div className={classes.formGroupGrid}>
                <TextField
                  fullWidth
                  label={t('password')}
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  onChange={(event) => handleFieldChange(event, 'password', event.target.value)}
                  value={newUser?.password}
                  variant="outlined"
                  placeholder={t('password')}
                  helperText={errors && errors.password}
                  error={!!(errors && errors.password)}
                  required
                />
              </div>
            </Grid>
            <Grid item xs={6}>
              <div className={classes.formGroupGrid}>
                <TextField
                  fullWidth
                  label={t('confirm_password')}
                  name="confirmPassword"
                  type="password"
                  autoComplete="confirm-password"
                  onChange={(event) => handleFieldChange(event, 'confirmPassword', event.target.value)}
                  value={newUser?.confirmPasword}
                  variant="outlined"
                  placeholder={t('confirm_password')}
                  helperText={errors && errors.confirmPassword}
                  error={!!(errors && errors.confirmPassword)}
                  required
                />
              </div>
            </Grid>
          </Grid>
        ) : (
          <RegistrationSuccess />
        )}
      </AuthCard>
    </form>
  );
};

export default RegisterForm;
